import localforage from 'localforage'

type PushConfig = {
  enabled?: boolean
  publicKey?: string | null
}

type PushSubscriptionBody = {
  endpoint: string
  expirationTime: number | null
  keys: {
    p256dh: string
    auth: string
  }
}

const STORAGE_KEY = 'dade_admin_push_endpoint'
const SERVICE_WORKER_URL = '/service-worker.js'
const PRINCIPAL_CACHE = 'dade-push-security-v1'
const PRINCIPAL_CACHE_REQUEST = '/__dade_push_principal__'

export async function setBrowserPushPrincipal(principalId: string | null) {
  if (typeof window === 'undefined' || !('caches' in window)) return

  const cache = await window.caches.open(PRINCIPAL_CACHE)
  if (!principalId) {
    await cache.delete(PRINCIPAL_CACHE_REQUEST)
    return
  }

  await cache.put(
    PRINCIPAL_CACHE_REQUEST,
    new Response(principalId, {
      headers: {'Content-Type': 'text/plain', 'Cache-Control': 'no-store'}
    })
  )
}

function urlBase64ToUint8Array(base64String: string) {
  const normalized = base64String.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4)
  const rawData = window.atob(`${normalized}${padding}`)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

function toSubscriptionBody(
  subscription: PushSubscription
): PushSubscriptionBody {
  const json = subscription.toJSON()
  const endpoint = String(json.endpoint || '').trim()
  const p256dh = String(json.keys?.p256dh || '').trim()
  const auth = String(json.keys?.auth || '').trim()

  if (!endpoint || !p256dh || !auth) {
    throw new Error('Invalid browser push subscription')
  }

  return {
    endpoint,
    expirationTime:
      typeof json.expirationTime === 'number' ? json.expirationTime : null,
    keys: {
      p256dh,
      auth
    }
  }
}

async function getServiceWorkerRegistration() {
  const existing = await navigator.serviceWorker.getRegistration()
  if (existing) return existing
  return navigator.serviceWorker.register(SERVICE_WORKER_URL)
}

export async function subscribeBrowserPush({
  principalId,
  loadConfig,
  saveSubscription
}: {
  principalId: string
  loadConfig: () => Promise<PushConfig>
  saveSubscription: (subscription: PushSubscriptionBody) => Promise<unknown>
}) {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return {status: 'unsupported' as const}
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return {status: permission}
  }

  const config = await loadConfig()
  if (!config?.enabled || !config.publicKey) {
    return {status: 'disabled' as const}
  }

  const registration = await getServiceWorkerRegistration()
  const currentSubscription = await registration.pushManager.getSubscription()
  const subscription =
    currentSubscription ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey)
    }))

  const body = toSubscriptionBody(subscription)
  await setBrowserPushPrincipal(principalId)
  try {
    await saveSubscription(body)
    await localforage.setItem(STORAGE_KEY, body.endpoint)
  } catch (error) {
    await Promise.allSettled([
      setBrowserPushPrincipal(null),
      subscription.unsubscribe(),
      localforage.removeItem(STORAGE_KEY)
    ])
    throw error
  }

  return {
    status: 'granted' as const,
    endpoint: body.endpoint
  }
}

export async function detachBrowserPushSubscription() {
  if (typeof window === 'undefined') return

  await setBrowserPushPrincipal(null).catch(() => {})

  let endpoint =
    ((await localforage.getItem(STORAGE_KEY).catch(() => null)) as
      | string
      | null) || undefined

  const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window
  if (!pushSupported) {
    await localforage.removeItem(STORAGE_KEY).catch(() => {})
    return endpoint
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    const subscription = await registration?.pushManager.getSubscription()
    if (subscription) endpoint = toSubscriptionBody(subscription).endpoint
    await subscription?.unsubscribe()
  } catch {
    // The local endpoint record is still removed below. The server cleanup can
    // use its saved value and terminal delivery errors disable stale rows.
  } finally {
    await localforage.removeItem(STORAGE_KEY).catch(() => {})
  }

  return endpoint
}

export async function clearBrowserPushSubscription({
  clearSubscription
}: {
  clearSubscription: (endpoint?: string) => Promise<unknown>
}) {
  const endpoint = await detachBrowserPushSubscription()

  if (endpoint) await clearSubscription(endpoint)
}
